import React, { useEffect, useMemo, useState } from 'react';
import allContribs from '../data/all-contributors.json';
import capitalCoords from '../data/capital_coords.json';
import { FlagPair } from './FlagPair';
import { useGeoDetection } from '../hooks/useGeoDetection';
import { useReducedMotion } from '../hooks/useReducedMotion';

type ContribEntry = {
  id: number | string;
  name: string;
  title: string;
  cc1: string;
  cc2: string | null;
  country1: string;
  country2: string | null;
  photo_url: string | null;
  tier: string;
};
type CapitalMap = Record<string, { lat: number; lon: number }>;

const ROTATE_MS = 6000;
const MAX_ROTATION = 6;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() ?? '')
    .join('');
}

function greatCircleKm(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number
): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * Build a prioritised rotation of contributors for the featured slot.
 *
 * Geo-awareness: match on EITHER cc1 or cc2. If the visitor is from
 * Thailand, show contributors where Thailand is on either side of the
 * bilateral pair (TH↔US, TH↔PL, SE↔TH, etc.).
 *
 * Distance scoring: for each contributor, take the minimum distance from
 * the visitor to either of their two country capitals. Sort ascending.
 */
function pickRotation(
  list: ContribEntry[],
  geo: { cc: string | null; lat: number | null; lon: number | null }
): ContribEntry[] {
  const caps = capitalCoords as CapitalMap;
  const eligible = list.filter(c => c.photo_url);
  if (!eligible.length) return list.slice(0, MAX_ROTATION);

  if (geo.lat == null || geo.lon == null) {
    return eligible.slice(0, MAX_ROTATION);
  }

  const visitorCC = (geo.cc || '').toUpperCase();

  const scored = eligible.map(c => {
    const k1 = c.cc1.toUpperCase();
    const k2 = c.cc2 ? c.cc2.toUpperCase() : '';
    const cap1 = caps[k1];
    const cap2 = k2 ? caps[k2] : null;

    // Min distance from visitor to either of this contributor's countries
    let minDist = Number.POSITIVE_INFINITY;
    if (cap1)
      minDist = Math.min(
        minDist,
        greatCircleKm(geo.lat!, geo.lon!, cap1.lat, cap1.lon)
      );
    if (cap2)
      minDist = Math.min(
        minDist,
        greatCircleKm(geo.lat!, geo.lon!, cap2.lat, cap2.lon)
      );

    // Home match: visitor's country is on either side of the pair
    const isHome = k1 === visitorCC || k2 === visitorCC;
    return { c, dist: isHome ? -1 : minDist };
  });

  scored.sort((a, b) => a.dist - b.dist);
  return scored.slice(0, MAX_ROTATION).map(s => s.c);
}

export function FeaturedDiplomatCard() {
  const geo = useGeoDetection();
  const reduced = useReducedMotion();
  const list = allContribs as ContribEntry[];

  const rotation = useMemo(() => pickRotation(list, geo), [list, geo]);

  const [idx, setIdx] = useState(0);
  const [errored, setErrored] = useState<Record<string | number, boolean>>({});
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    setIdx(0);
  }, [rotation]);

  useEffect(() => {
    if (reduced || paused || rotation.length <= 1) return;
    const id = window.setInterval(() => {
      setIdx(i => (i + 1) % rotation.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [reduced, paused, rotation.length]);

  // Preload next photo
  useEffect(() => {
    if (rotation.length <= 1) return;
    const next = rotation[(idx + 1) % rotation.length];
    if (!next?.photo_url) return;
    const img = new Image();
    img.src = next.photo_url;
  }, [idx, rotation]);

  const chief = rotation[idx];
  if (!chief) return null;
  const showInitials = !chief.photo_url || errored[chief.id];

  const pairLabel = chief.cc2
    ? `${chief.country1} · ${chief.country2}`
    : chief.country1;

  return (
    <article
      className="featured-diplomat"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '112px 1fr',
        gap: 'var(--space-lg)',
        alignItems: 'center',
        padding: 'var(--space-lg)',
        background:
          'linear-gradient(180deg, rgba(20, 28, 48, 0.55), rgba(20, 28, 48, 0.25))',
        border: '1px solid var(--color-border)',
        borderRadius: 4,
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        maxWidth: 440
      }}
      aria-live="polite"
    >
      <div
        style={{
          width: 112,
          height: 112,
          borderRadius: '50%',
          overflow: 'hidden',
          background: 'rgba(184, 155, 100, 0.12)',
          border: '1px solid var(--color-accent-dim)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-accent)',
          fontFamily: 'var(--font-display)',
          fontSize: 32
        }}
      >
        {showInitials ? (
          <span key={`init-${chief.id}`}>{initials(chief.name)}</span>
        ) : (
          <img
            key={chief.id}
            src={chief.photo_url!}
            alt={chief.name}
            loading="eager"
            decoding="async"
            onError={() =>
              setErrored(prev => ({ ...prev, [chief.id]: true }))
            }
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              animation: reduced ? 'none' : 'featured-fade 0.45s ease-out'
            }}
          />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 11,
            color: 'var(--color-accent)',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8
          }}
        >
          <span>Featured Contributor</span>
          {rotation.length > 1 && (
            <span aria-hidden="true" style={{ display: 'inline-flex', gap: 4 }}>
              {rotation.map((r, i) => (
                <span
                  key={r.id}
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    background:
                      i === idx
                        ? 'var(--color-accent)'
                        : 'var(--color-accent-dim)',
                    transition: 'background 0.3s'
                  }}
                />
              ))}
            </span>
          )}
        </div>
        <div
          key={`name-${chief.id}`}
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1.2,
            marginBottom: 4,
            animation: reduced ? 'none' : 'featured-fade 0.45s ease-out'
          }}
        >
          {chief.name}
        </div>
        <div
          key={`title-${chief.id}`}
          style={{
            fontSize: 13,
            color: 'var(--color-muted)',
            lineHeight: 1.4,
            marginBottom: 10,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 36,
            animation: reduced ? 'none' : 'featured-fade 0.45s ease-out'
          }}
        >
          {chief.title || '—'}
        </div>
        <div
          key={`meta-${chief.id}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            animation: reduced ? 'none' : 'featured-fade 0.45s ease-out'
          }}
        >
          <FlagPair cc1={chief.cc1} cc2={chief.cc2} height={16} />
          <span
            style={{
              fontSize: 12,
              color: 'var(--color-text)',
              letterSpacing: '0.02em'
            }}
          >
            {pairLabel}
          </span>
        </div>
      </div>
      <style>{`
        @keyframes featured-fade {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </article>
  );
}
